import { Col, Form, Row, Typography } from 'antd';
import React, { ReactElement, useEffect } from 'react';
import CcxComponentProps from '../../../core/CcxComponent';
import { CDN_URL, hideCloudProvider } from '../../../core/CcxEnv';
import CloudProvider from '../../../types/CloudProvider';
import CloudProviderRegion from '../../../types/CloudProviderRegion';
import AppFlagIcon from '../../ccx/common/AppFlagIcon';
import styles from './WizardFormConfigurationStep2.module.less';
import AppRadioGroupGrid, {
    AppRadioGridButton,
} from '../../ccx/common/Form/AppRadioGroupGrid';

interface Props extends CcxComponentProps {
    handleCloudProviderChange: Function;
    cloudProviders?: CloudProvider[];
    regions?: CloudProviderRegion[];
    handleRegionChange: Function;
    form?: any;
}

export function WizardFormConfigurationStep2({
    testId = 'WizardFormConfigurationStep2',
    handleCloudProviderChange,
    cloudProviders,
    regions,
    handleRegionChange,
    form,
}: Props): ReactElement {
    const getCloudProviderLogo = (p: CloudProvider): string => {
        if (p.isAws()) return `${CDN_URL}provider-aws-brand-logo-short.svg`;
        if (p.isSafespring())
            return `${CDN_URL}provider-safespring-brand-logo-short.svg`;
        if (p.isCityCloud())
            return `${CDN_URL}provider-citycloud-brand-logo-short.svg`;
        if (p.isGoogle())
            return `${CDN_URL}provider-googlecloud-brand-logo-short.svg`;
        if (p.isVultr()) return `${CDN_URL}provider-vultr-brand-logo-short.svg`;
        if (p.isLintasarta())
            return `${CDN_URL}provider-lintasarta-brand-logo-short.svg`;
        if (p.isElastx())
            return `${CDN_URL}provider-elastx-brand-logo-short.svg`;
        if (p.isSyseleven())
            return `${CDN_URL}provider-syseleven-brand-logo-short.svg`;
        return '';
    };

    const { Text } = Typography;

    useEffect(() => {
        if (cloudProviders && cloudProviders.length === 1) {
            form.setFieldsValue({
                cloudProvider: cloudProviders[0],
            });
            handleCloudProviderChange({ target: { value: cloudProviders[0] } });
        }
    }, [cloudProviders]);

    useEffect(() => {
        if (regions && regions.length === 1) {
            form.setFieldsValue({
                region: regions[0],
            });
            handleRegionChange({ target: { value: regions[0] } });
        }
    }, [regions]);

    return (
        <div data-testid={testId}>
            {!hideCloudProvider && (
                <>
                    <p>
                        <strong>Cloud service provider</strong>
                    </p>

                    <Form.Item
                        name="cloudProvider"
                        label=""
                        rules={[
                            {
                                required: true,
                                message: 'Select your preferred cloud provider',
                            },
                        ]}
                    >
                        <AppRadioGroupGrid
                            data-testid={`${testId}CloudProviderRadio`}
                            onChange={handleCloudProviderChange as any}
                            items={
                                cloudProviders?.map((p: CloudProvider) => {
                                    return (
                                        <AppRadioGridButton
                                            data-testid={`${testId}CloudProviderRadio${p.code}`}
                                            value={p}
                                        >
                                            <div>
                                                <img
                                                    src={getCloudProviderLogo(
                                                        p
                                                    )}
                                                    alt={p.code}
                                                    height={24}
                                                    width={44}
                                                />
                                                <small>{p.name}</small>
                                            </div>
                                        </AppRadioGridButton>
                                    );
                                }) || []
                            }
                        />
                    </Form.Item>
                </>
            )}

            {regions?.length && (
                <>
                    <p>
                        <strong>Regions</strong>
                    </p>

                    <Form.Item
                        name="region"
                        label=""
                        rules={[
                            {
                                required: true,
                                message: 'Select your preferred region',
                            },
                        ]}
                    >
                        <AppRadioGroupGrid
                            data-testid={`${testId}RegionRadio`}
                            onChange={handleRegionChange as any}
                            items={
                                regions.map((p: CloudProviderRegion) => {
                                    return (
                                        <AppRadioGridButton
                                            data-testid={`${testId}RegionsRadio${p.code}`}
                                            value={p}
                                            size={'large'}
                                        >
                                            <Row align="middle">
                                                <Col span={4}>
                                                    <AppFlagIcon
                                                        code={p.countryCode}
                                                    />
                                                </Col>
                                                <Col span={20}>
                                                    <div>
                                                        <div
                                                            className={
                                                                styles.WizardFormConfigurationStep2RegionLabel
                                                            }
                                                        >
                                                            <small>
                                                                {p.displayCode}
                                                            </small>
                                                            <Text type="secondary">
                                                                <small>
                                                                    {p.city}
                                                                </small>
                                                            </Text>
                                                        </div>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </AppRadioGridButton>
                                    );
                                }) || []
                            }
                        />
                    </Form.Item>
                </>
            )}
        </div>
    );
}
